import React, { useContext, useState, useEffect } from "react";
import PageLayout from "../../../layouts/PageLayout";
import { Container } from "react-bootstrap";
import { TranslatorContext } from "../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getSessionExpiry } from "../../../utils/session";

const pad = (value) => String(value).padStart(2, "0");

// Milliseconds left on the login session as "2h 05m 12s".
const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`;

  return `${seconds}s`;
};

const formatClock = (timestamp) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(timestamp));

export default function DashboardPage() {
  const { t } = useContext(TranslatorContext);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    purchases: 0,
    sales: 0,
    totalAppointments: 0,
    products: 0,
    appointmentsTotalAmount: 0,
    appointmentsPaidAmount: 0,
    appointmentsUnpaidAmount: 0,
    reminderVaccine: [],
    reminderDeworming: [],
    reminderGrooming: [],
    nextReminder: null,
    reminderSummary: null
  });

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: ""
  });

  // Date range the cards were last loaded with; the detail page uses the same range.
  const [appliedFilters, setAppliedFilters] = useState({ start_date: "", end_date: "" });
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  // Time left before the login session expires, ticked every second.
  const [sessionExpiry] = useState(getSessionExpiry);
  const [sessionLeft, setSessionLeft] = useState(() =>
    sessionExpiry ? sessionExpiry - Date.now() : null
  );

  useEffect(() => {
    if (!sessionExpiry) return undefined;

    const tick = () => setSessionLeft(sessionExpiry - Date.now());

    tick();
    const timer = setInterval(tick, 1000);

    return () => clearInterval(timer);
  }, [sessionExpiry]);

  useEffect(() => {
    const todayFilters = {
      start_date: getTodayDate(),
      end_date: getTodayDate()
    };

    setFilters(todayFilters);
    fetchDashboardData(todayFilters);
  }, []);

  const buildRangeParams = (filterParams = {}) => {
    const params = new URLSearchParams();
    if (filterParams.start_date) params.append('start_date', filterParams.start_date);
    if (filterParams.end_date) params.append('end_date', filterParams.end_date);
    return params;
  };

  const fetchDashboardData = async (filterParams = {}) => {
    setLoading(true);
    setAppliedFilters({ start_date: filterParams.start_date || "", end_date: filterParams.end_date || "" });
    try {
      const params = buildRangeParams(filterParams);

      const url = `${process.env.REACT_APP_API_URL}/dashboard${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        }
      });

      if (response.data.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchDashboardData(filters);
  };

  const openDetail = (key) => {
    const params = buildRangeParams(appliedFilters);
    navigate(`/dashboard-details/${key}${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const resetFilters = () => {
    const today = getTodayDate();
    const resetData = {
      start_date: today,
      end_date: today
    };

    setFilters(resetData);
    fetchDashboardData(resetData);
  };

  const formatAmount = (value) =>
    parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  const formatDate = (value) => {
    if (!value) return "-";

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).format(new Date(value));
  };

  const dueLabel = (item) => {
    if (!item) return "";
    if (item.is_today) return t("Due today") || "Due today";
    if (item.days_left === 1) return t("Tomorrow") || "Tomorrow";
    if (item.days_left > 1) return `${item.days_left} ${t("days left") || "days left"}`;

    return t("Overdue") || "Overdue";
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="db-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading Dashboard...</p>
        </div>
      </PageLayout>
    );
  }

  const summary = dashboardData.reminderSummary;
  const nextReminder = dashboardData.nextReminder;

  const progressPercent = dashboardData.appointmentsTotalAmount > 0
    ? (dashboardData.appointmentsPaidAmount / dashboardData.appointmentsTotalAmount) * 100
    : 0;
  const salesProgressPercent = dashboardData.salesTotalAmount > 0
    ? (dashboardData.salesTotalPaidAmount / dashboardData.salesTotalAmount) * 100
    : 0;

  const safePercent = Math.min(Math.max(progressPercent, 0), 100);
  const salesSafePercent = Math.min(Math.max(salesProgressPercent, 0), 100);

  const kpis = [
    {
      key: "sales",
      tone: "is-blue",
      icon: "point_of_sale",
      value: dashboardData.sales,
      label: t("Sales") || "Sales",
      note: t("sales count") || "Number of sales"
    },
    {
      key: "appointments",
      tone: "is-orange",
      icon: "calendar_today",
      value: dashboardData.totalAppointments,
      label: t("Appointments") || "Appointments",
      note: t("appointments count") || "Number of appointments"
    },
    {
      key: "billed",
      tone: "is-dark",
      icon: "receipt_long",
      value: formatAmount(dashboardData.appointmentsTotalAmount + dashboardData.salesTotalAmount),
      label: t("Total Bill") || "Total Bill",
      note: t("Sales and appointments") || "Sales and appointments"
    },
    {
      key: "paid",
      tone: "is-blue",
      icon: "payments",
      value: formatAmount(dashboardData.appointmentsPaidAmount + dashboardData.salesTotalPaidAmount),
      label: t("Money Received") || "Money Received",
      note: `${t("Paid so far") || "Paid so far"}`
    },
    {
      key: "unpaid",
      tone: "is-orange",
      icon: "account_balance_wallet",
      value: formatAmount(dashboardData.appointmentsUnpaidAmount + dashboardData.salesUnpaidAmount),
      label: t("Not Paid Yet") || "Not Paid Yet",
      note: t("Still to collect") || "Still to collect"
    }
  ];

  const reminderCards = [
    {
      key: "vaccine",
      title: t("Vaccine Reminders") || "Vaccine Reminders",
      icon: "💉",
      tone: "is-blue",
      items: dashboardData.reminderVaccine || []
    },
    {
      key: "deworming",
      title: t("Deworming Reminders") || "Deworming Reminders",
      icon: "🐛",
      tone: "is-orange",
      items: dashboardData.reminderDeworming || []
    },
    {
      key: "grooming",
      title: t("Grooming Reminders") || "Grooming Reminders",
      icon: "✂️",
      tone: "is-dark",
      items: dashboardData.reminderGrooming || []
    }
  ];

  return (
    <PageLayout>
      <Container fluid className="px-0">
        <div className="db-page">
          <section className="db-hero">
            <div className="db-hero-inner">
              <div>
                <span className="db-hero-eyebrow">{t("home") || "Home"}</span>
                <h3 className="db-hero-title">{t("Dashboard") || "Dashboard"}</h3>
                <span className="db-hero-sub">
                  {summary
                    ? `${summary.total} ${t("reminders this month") || "reminders this month"} ${summary.month_label}`
                    : t("Overview") || "Overview"}
                </span>

                {sessionExpiry && (
                  <span
                    className={`db-session ${sessionLeft <= 0
                      ? "is-expired"
                      : sessionLeft <= 15 * 60 * 1000
                        ? "is-warning"
                        : ""
                      }`}
                    title={`${t("Session expires at") || "Session expires at"} ${formatClock(sessionExpiry)}`}
                  >
                    <i className="material-icons">schedule</i>
                    {sessionLeft <= 0 ? (
                      <>{t("Session expired") || "Session expired"}</>
                    ) : (
                      <>
                        {t("Session ends in") || "Session ends in"}
                        <strong>{formatCountdown(sessionLeft)}</strong>
                      </>
                    )}
                  </span>
                )}
              </div>

              <div className="db-filter">
                <div>
                  <label htmlFor="db-start">{t("Start Date") || "Start Date"}</label>
                  <input
                    id="db-start"
                    type="date"
                    name="start_date"
                    value={filters.start_date}
                    onChange={handleFilterChange}
                  />
                </div>

                <div>
                  <label htmlFor="db-end">{t("End Date") || "End Date"}</label>
                  <input
                    id="db-end"
                    type="date"
                    name="end_date"
                    value={filters.end_date}
                    onChange={handleFilterChange}
                  />
                </div>

                <button type="button" className="db-filter-btn is-solid" onClick={applyFilters}>
                  <i className="material-icons">search</i>
                  {t("Apply") || "Apply"}
                </button>

                <button type="button" className="db-filter-btn" onClick={resetFilters}>
                  <i className="material-icons">refresh</i>
                  {t("Reset") || "Reset"}
                </button>
              </div>
            </div>
          </section>

          {isAdmin && (
            <>
              <div className="db-kpis">
                {kpis.map((kpi) => (
                  <button
                    type="button"
                    className={`db-kpi ${kpi.tone}`}
                    key={kpi.key}
                    onClick={() => openDetail(kpi.key)}
                    style={{ border: "none", textAlign: "left", font: "inherit", cursor: "pointer", width: "100%" }}
                  >
                    <span className="db-kpi-icon">
                      <i className="material-icons">{kpi.icon}</i>
                    </span>
                    <span className="db-kpi-value">{kpi.value}</span>
                    <span className="db-kpi-label">{kpi.label}</span>
                    <span className="db-kpi-note">{kpi.note}</span>
                  </button>
                ))}
              </div>

              <div className="db-collection">
                <div className="db-collection-head">
                  <h5 className="db-collection-title">
                    {t("Appointment Payments") || "Appointment Payments"}
                  </h5>
                  <span className="db-collection-value">{safePercent.toFixed(0)}%</span>
                </div>

                <div className="db-bar">
                  <div className="db-bar-fill" style={{ width: `${safePercent}%` }} />
                </div>

                <div className="db-collection-legend">
                  <span>
                    {t("Total") || "Total"}
                    <strong>{formatAmount(dashboardData.appointmentsTotalAmount)}</strong>
                  </span>
                  <span>
                    {t("Received") || "Received"}
                    <strong>{formatAmount(dashboardData.appointmentsPaidAmount)}</strong>
                  </span>
                  <span>
                    {t("Not Paid") || "Not Paid"}
                    <strong>{formatAmount(dashboardData.appointmentsUnpaidAmount)}</strong>
                  </span>
                </div>
              </div>
              <div className="db-collection">
                <div className="db-collection-head">
                  <h5 className="db-collection-title">
                    {t("Sales Payments") || "Sales Payments"}
                  </h5>
                  <span className="db-collection-value">{salesSafePercent.toFixed(0)}%</span>
                </div>

                <div className="db-bar">
                  <div className="db-bar-fill" style={{ width: `${salesSafePercent}%` }} />
                </div>

                <div className="db-collection-legend">
                  <span>
                    {t("Total") || "Total"}
                    <strong>{formatAmount(dashboardData.salesTotalAmount)}</strong>
                  </span>
                  <span>
                    {t("Received") || "Received"}
                    <strong>{formatAmount(dashboardData.salesTotalPaidAmount)}</strong>
                  </span>
                  <span>
                    {t("Not Paid") || "Not Paid"}
                    <strong>{formatAmount(dashboardData.salesUnpaidAmount)}</strong>
                  </span>
                </div>
              </div>
            </>
          )}

          <Link to="/reminders" className={`db-next ${nextReminder ? "" : "is-empty"}`}>
            <div className="db-next-body">
              <span className="db-next-icon">{nextReminder ? "🔔" : "✅"}</span>
              <div>
                <span className="db-next-label">
                  {t("Next reminder") || "Next reminder"}
                </span>
                <h4 className="db-next-title">
                  {nextReminder
                    ? `${nextReminder.pet_name || "-"} · ${nextReminder.item_name}`
                    : t("No reminders due this month") || "No reminders due this month"}
                </h4>

                {nextReminder && (
                  <div className="db-next-meta">
                    <span>{formatDate(nextReminder.due_date)}</span>
                    <span>{dueLabel(nextReminder)}</span>
                    <span>{nextReminder.type}</span>
                    {nextReminder.owner_name && <span>{nextReminder.owner_name}</span>}
                    {nextReminder.owner_phone && <span>{nextReminder.owner_phone}</span>}
                  </div>
                )}
              </div>
            </div>

            <span className="db-next-cta">
              {t("Open reminders") || "Open reminders"}
              <i className="material-icons" style={{ fontSize: "15px" }}>arrow_forward</i>
            </span>
          </Link>

          <div className="db-section-title">
            <h4>{t("Upcoming Reminders") || "Upcoming Reminders"}</h4>
            {summary && (
              <span>
                {formatDate(summary.period_start)} – {formatDate(summary.period_end)} ·{" "}
                {summary.due_today} {t("due today") || "due today"}
              </span>
            )}
          </div>

          <div className="db-reminders">
            {reminderCards.map((card) => (
              <Link to="/reminders" className={`db-rem-card ${card.tone}`} key={card.key}>
                <div className="db-rem-head">
                  <span className="db-rem-icon">{card.icon}</span>
                  <div>
                    <h4 className="db-rem-title">{card.title}</h4>
                    <span className="db-rem-sub">
                      {card.items.length > 0
                        ? `${t("due in") || "due in"} ${summary?.month_label || ""}`
                        : t("Nothing due this month") || "Nothing due this month"}
                    </span>
                  </div>
                  <span className="db-rem-count">
                    <b>{card.items.length}</b>
                  </span>
                </div>

                <div className="db-rem-body">
                  {card.items.length > 0 ? (
                    card.items.slice(0, 4).map((item) => (
                      <div className="db-rem-row" key={item.id}>
                        <span className="db-rem-pet">
                          <strong>{item.pet_name || "-"}</strong>
                          <small>
                            {item.item_name}
                            {item.owner_name ? ` · ${item.owner_name}` : ""}
                          </small>
                        </span>
                        <span className="db-rem-when">
                          <strong>{formatDate(item.due_date)}</strong>
                          <small className={item.is_today ? "is-today" : ""}>{dueLabel(item)}</small>
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="db-rem-empty">
                      {t("No reminders scheduled") || "No reminders scheduled"}
                    </div>
                  )}

                  {card.items.length > 4 && (
                    <div className="db-rem-row">
                      <span className="db-rem-sub">
                        +{card.items.length - 4} {t("more") || "more"}
                      </span>
                    </div>
                  )}
                </div>

                <span className="db-rem-link">{t("View all reminders") || "View all reminders"}</span>
              </Link>
            ))}
          </div>
        </div>

      </Container>
    </PageLayout>
  );
}
